import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { createPortal } from "react-dom";
import Picker from "emoji-picker-react";
import { comment } from "../../functions/post";
import { uploadImages } from "../../functions/uploadImages";
import dataURItoBlob from "../../helpers/dataURItoBlob";
import { ClipLoader } from "react-spinners";

const CreateComment = forwardRef(function CreateComment({ user, postId, setComments, setCount }, ref) {
  const [picker, setPicker] = useState(false);
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const [commentImage, setCommentImage] = useState("");
  const [cursorPosition, setCursorPosition] = useState();
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  
  // GIF and Sticker states
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [showStickerPicker, setShowStickerPicker] = useState(false);
  const [gifSearch, setGifSearch] = useState("");
  const [gifs, setGifs] = useState([]);
  const [selectedGif, setSelectedGif] = useState(null);
  const [selectedSticker, setSelectedSticker] = useState(null);
  const [gifLoading, setGifLoading] = useState(false);
  const gifSearchInputRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const emojiButtonRef = useRef(null);
  const wasPickerOpenRef = useRef(false);
  
  const textRef = useRef(null);
  const imgInput = useRef(null);
  const caretRef = useRef(0);
  const textValueRef = useRef("");
  const isMobileView = () =>
    typeof window !== "undefined" && window.innerWidth <= 768;

  // Expose focus and scrollIntoView to parent (e.g. when Comment button is clicked)
  useImperativeHandle(ref, () => ({
    focus: () => {
      if (textRef.current) {
        textRef.current.focus();
        textRef.current.closest?.(".comment_input_wrap")?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
      }
    },
  }));

  useEffect(() => {
    // Only restore caret when the input is focused (desktop). Refocusing on mobile
    // would reopen the keyboard while the emoji picker is in use.
    if (
      textRef.current &&
      typeof cursorPosition === "number" &&
      document.activeElement === textRef.current
    ) {
      textRef.current.selectionStart = cursorPosition;
      textRef.current.selectionEnd = cursorPosition;
    }
  }, [cursorPosition]);

  // Close other pickers when one is opened
  useEffect(() => {
    if (showGifPicker) {
      setPicker(false);
      setShowStickerPicker(false);
    }
  }, [showGifPicker]);

  useEffect(() => {
    if (showStickerPicker) {
      setPicker(false);
      setShowGifPicker(false);
    }
  }, [showStickerPicker]);

  useEffect(() => {
    if (picker) {
      setShowGifPicker(false);
      setShowStickerPicker(false);
    }
  }, [picker]);

  // On mobile: hide keyboard while emoji picker is open. Do not refocus on close.
  useEffect(() => {
    if (!isMobileView()) {
      wasPickerOpenRef.current = picker;
      return;
    }

    if (picker) {
      textRef.current?.blur?.();
    }

    wasPickerOpenRef.current = picker;
  }, [picker]);

  const syncCaretFromInput = () => {
    if (textRef.current && typeof textRef.current.selectionStart === "number") {
      caretRef.current = textRef.current.selectionStart;
    }
  };

  const handleEmoji = (event, emojiObject) => {
    const emoji = emojiObject?.emoji;
    if (!emoji) return;

    event?.preventDefault?.();
    event?.stopPropagation?.();

    const mobile = isMobileView();
    // Keep keyboard closed on mobile while picking more emojis
    if (!mobile && textRef.current) {
      textRef.current.focus();
      syncCaretFromInput();
    } else {
      textRef.current?.blur?.();
    }

    const prev = textValueRef.current || "";
    const caret =
      typeof caretRef.current === "number" ? caretRef.current : prev.length;
    const start = prev.substring(0, caret);
    const end = prev.substring(caret);
    const nextText = start + emoji + end;
    const nextCaret = start.length + emoji.length;

    textValueRef.current = nextText;
    caretRef.current = nextCaret;
    setText(nextText);
    setCursorPosition(nextCaret);
    // Keep picker open so user can pick more; send via picker Send button
  };

  const deleteLastEmoji = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    const prev = textValueRef.current || "";
    if (!prev) return;

    let next = "";
    if (typeof Intl !== "undefined" && Intl.Segmenter) {
      const segments = [
        ...new Intl.Segmenter(undefined, { granularity: "grapheme" }).segment(
          prev
        ),
      ];
      segments.pop();
      next = segments.map((s) => s.segment).join("");
    } else {
      const chars = [...prev];
      chars.pop();
      next = chars.join("");
    }

    textValueRef.current = next;
    caretRef.current = next.length;
    setText(next);
    setCursorPosition(next.length);
  };

  const sendFromEmojiPicker = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!textValueRef.current?.trim() && !text.trim()) return;
    setPicker(false);
    submitComment({ textOverride: textValueRef.current || text });
  };

  const closeEmojiPicker = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setPicker(false);
  };

    // GIF API integration
  const GIPHY_API_KEY = "GlVGYHkr3WSBnllca54iNt0yFbjz7L65";

  const loadTrendingGifs = async () => {
    setGifLoading(true);
    setError("");
    try {
      const response = await fetch(
        `https://api.giphy.com/v1/gifs/trending?api_key=${GIPHY_API_KEY}&limit=24&rating=g`
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (data.data && Array.isArray(data.data) && data.data.length > 0) {
        setGifs(data.data);
      } else {
        setGifs([]);
      }
    } catch (error) {
      console.error("Error fetching trending GIFs:", error);
      setGifs([]);
      setError("Could not load GIFs. Try searching or try again later.");
    } finally {
      setGifLoading(false);
    }
  };

  const searchGifs = async (query) => {
    if (!query.trim()) {
      loadTrendingGifs();
      return;
    }
    
    setGifLoading(true);
    setError(""); // Clear any previous errors
    
    try {
      // Using Giphy API with a working API key
      // For production, get a free API key from https://developers.giphy.com/
      const response = await fetch(
        `https://api.giphy.com/v1/gifs/search?api_key=${GIPHY_API_KEY}&q=${encodeURIComponent(query)}&limit=24&rating=g`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.data && Array.isArray(data.data) && data.data.length > 0) {
        setGifs(data.data);
      } else {
        setGifs([]);
        setError("No GIFs found for your search.");
      }
    } catch (error) {
      console.error("Error fetching GIFs:", error);
      setGifs([]);
      setError("GIF search failed. Please try again later.");
    } finally {
      setGifLoading(false);
    }
  };

  const handleGifSearch = (e) => {
    const query = e.target.value;
    setGifSearch(query);
    
    // Clear any previous errors when starting a new search
    if (query.trim() !== gifSearch.trim()) {
      setError("");
    }
    
    // Debounce search; empty query restores trending GIFs
    clearTimeout(gifSearchTimeout.current);
    gifSearchTimeout.current = setTimeout(() => {
      if (query.trim()) {
        searchGifs(query);
      } else {
        loadTrendingGifs();
      }
    }, 400);
  };

  const retryGifSearch = () => {
    setError("");
    if (gifSearch.trim()) {
      searchGifs(gifSearch);
    } else {
      loadTrendingGifs();
    }
  };

  const gifSearchTimeout = useRef(null);

  const closeGifPicker = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setShowGifPicker(false);
    setGifSearch("");
    setError("");
  };

  // When GIF picker opens: load trending GIFs (no search needed). Don't autofocus on mobile.
  useEffect(() => {
    if (!showGifPicker) return;

    loadTrendingGifs();

    if (!isMobileView() && gifSearchInputRef.current) {
      setTimeout(() => gifSearchInputRef.current?.focus(), 50);
    } else {
      textRef.current?.blur?.();
      gifSearchInputRef.current?.blur?.();
    }
  }, [showGifPicker]);

  const handleGifSelect = (gif, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setGifSearch("");
    setShowGifPicker(false);
    // Send immediately — no preview step
    submitComment({ gifOverride: gif, stickerOverride: null });
  };

  const handleStickerSelect = (sticker, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setShowStickerPicker(false);
    // Send immediately — no preview step
    submitComment({ stickerOverride: sticker, gifOverride: null });
  };

  const closeStickerPicker = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setShowStickerPicker(false);
  };

  // Sticker categories
  const stickerCategories = [
    { id: "emoji", name: "Emoji", stickers: ["😀", "😃", "😄", "😁", "😆", "😅", "😂", "🤣", "😊", "😇", "🙂", "🙃", "😉", "😌", "😍", "🥰", "😘", "😗", "😙", "😚", "😋", "😛", "😝", "😜", "🤪", "🤨", "🧐", "🤓", "😎", "🤩", "🥳"] },
    { id: "animals", name: "Animals", stickers: ["🐶", "🐱", "🐭", "🐹", "🐰", "🦊", "🐻", "🐼", "🐨", "🐯", "🦁", "🐮", "🐷", "🐸", "🐵", "🐔", "🐧", "🐦", "🐤", "🦆", "🦅", "🦉", "🦇", "🐺", "🐗", "🐴", "🦄", "🐝", "🐛", "🦋", "🐌"] },
    { id: "food", name: "Food", stickers: ["🍎", "🍐", "🍊", "🍋", "🍌", "🍉", "🍇", "🍓", "🍈", "🍒", "🍑", "🥭", "🍍", "🥥", "🥝", "🍅", "🥑", "🍆", "🥔", "🥕", "🌽", "🌶️", "🥒", "🥬", "🥦", "🧄", "🧅", "🥜", "🌰", "🥑", "🍞"] },
    { id: "activities", name: "Activities", stickers: ["⚽", "🏀", "🏈", "⚾", "🥎", "🎾", "🏐", "🏉", "🥏", "🎱", "🪀", "🏓", "🏸", "🏒", "🏑", "🥍", "🏏", "🥅", "⛳", "🪁", "🏹", "🎣", "🤿", "🥊", "🥋", "🎽", "🛹", "🛷", "⛸️", "🥌", "🎿"] }
  ];

  const handleImage = (e) => {
    let file = e.target.files[0];
    
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/avif"];
    if (!allowedTypes.includes(file.type)) {
      setError(`${file.name} format is not supported. Please use JPG, PNG, WEBP, GIF, or AVIF.`);
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 1024 * 1024 * 5) {
      setError(`${file.name} is too large. Maximum size allowed is 5MB.`);
      return;
    }

    setError(""); // Clear any previous errors

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      setCommentImage(event.target.result);
      setImagePreview({
        name: file.name,
        size: (file.size / 1024 / 1024).toFixed(2), // Size in MB
        type: file.type
      });
    };
  };

  const removeImage = () => {
    setCommentImage("");
    setImagePreview(null);
    if (imgInput.current) {
      imgInput.current.value = '';
    }
  };

  const handleComment = async (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault(); // Prevent new line
      await submitComment();
    }
  };

  const handleSubmitClick = async () => {
    await submitComment();
  };

  const submitComment = async ({
    textOverride,
    gifOverride,
    stickerOverride,
  } = {}) => {
    const commentText =
      textOverride !== undefined ? textOverride : text;
    const gifToSend =
      gifOverride !== undefined ? gifOverride : selectedGif;
    const stickerToSend =
      stickerOverride !== undefined ? stickerOverride : selectedSticker;

    // Don't submit if all content is empty
    if (!commentText.trim() && !commentImage && !gifToSend && !stickerToSend) {
      return;
    }

    // Prevent multiple submissions
    if (loading) {
      return;
    }

    setLoading(true);
    setError("");
    setSelectedGif(null);
    setSelectedSticker(null);

    try {
      let imageUrl = "";
      let gifUrl = "";
      let stickerUrl = "";
      
      if (commentImage) {
        const img = dataURItoBlob(commentImage, `comment_image_${Date.now()}.jpg`);
        const path = `${user.username}/post_images/${postId}`;
        let formData = new FormData();
        formData.append("path", path);
        formData.append("file", img);
        
        const imgComment = await uploadImages(formData, path, user.token);
        imageUrl = imgComment[0]?.url || "";
      }

      if (gifToSend) {
        gifUrl = gifToSend.images.fixed_height.url;
      }

      if (stickerToSend) {
        stickerUrl = stickerToSend;
      }

      if (!commentText.trim() && !imageUrl && !gifUrl && !stickerUrl) {
        setError("Please add some content to your comment");
        setLoading(false);
        return;
      }

      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), 10000)
      );
      
      const commentPromise = comment(
        postId,
        commentText,
        imageUrl,
        user.token,
        gifUrl,
        stickerUrl
      );
      const comments = await Promise.race([commentPromise, timeoutPromise]);
      
      if (typeof comments === "string") {
        setError(comments);
      } else if (Array.isArray(comments)) {
        const newComment = comments[comments.length - 1];
        if (newComment) {
          const withTime = { ...newComment, commentAt: newComment.commentAt || new Date().toISOString() };
          setComments(prev => {
            if (!Array.isArray(prev)) return comments;
            const filtered = prev.filter(c => (c._id || c.id || c.tempId) !== (withTime._id || withTime.id || withTime.tempId));
            return [withTime, ...filtered];
          });
        } else {
          setComments(comments);
        }
        setCount(prev => (typeof prev === 'number' ? prev + 1 : 1));
        setText("");
        textValueRef.current = "";
        caretRef.current = 0;
        removeImage();
        setPicker(false);
        setShowGifPicker(false);
        setShowStickerPicker(false);
      } else if (comments && typeof comments === 'object') {
        const newComment = { ...comments, commentAt: comments.commentAt || new Date().toISOString() };
        setComments(prev => (Array.isArray(prev) ? [newComment, ...prev] : [newComment]));
        setCount(prev => (typeof prev === 'number' ? prev + 1 : 1));
        setText("");
        textValueRef.current = "";
        caretRef.current = 0;
        removeImage();
        setPicker(false);
        setShowGifPicker(false);
        setShowStickerPicker(false);
      } else {
        setError("Invalid response from server");
      }
    } catch (error) {
      console.error("Comment submission error:", error);
      if (error.message === 'Request timeout') {
        setError("Request timed out. Please try again.");
      } else {
        setError("Failed to post comment. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create_comment_wrap">
      <div className="create_comment">
        <img src={user?.picture} alt="" />
        <div className="comment_input_wrap">
          {/* Hidden file input */}
          <input
            type="file"
            hidden
            ref={imgInput}
            accept="image/jpeg,image/png,image/gif,image/webp,image/avif"
            onChange={handleImage}
          />
          
          {/* Error display */}
          {error && (
            <div className="postError comment_error">
              <div className="postError_error">{error}</div>
              <button className="blue_btn" onClick={() => setError("")}>
                Try again
              </button>
            </div>
          )}
          
          {/* Comment input */}
          <input
            type="text"
            ref={textRef}
            value={text}
            readOnly={picker}
            inputMode={picker ? "none" : "text"}
            placeholder={commentImage ? "Add a caption.." : "Write a comment."}
            onChange={(e) => {
              setText(e.target.value);
              textValueRef.current = e.target.value;
              caretRef.current = e.target.selectionStart ?? e.target.value.length;
            }}
            onClick={syncCaretFromInput}
            onKeyUp={syncCaretFromInput}
            onSelect={syncCaretFromInput}
            onBlur={syncCaretFromInput}
            onKeyDown={handleComment}
            onFocus={() => {
              // If emoji picker is open, keep keyboard dismissed
              if (picker && isMobileView()) {
                textRef.current?.blur?.();
              }
            }}
            disabled={loading}
          />
          
          {/* Action buttons */}
          <div className="comment_actions_row">
            {/* Loading spinner */}
            {loading && (
              <div className="comment_circle">
                <ClipLoader size={20} color="#1876f2" loading={true} />
              </div>
            )}
            
            {/* Submit button - only show if there's content */}
            {(text.trim() || commentImage) && !loading && (
              <div 
                className="comment_circle_icon hover2 submit_comment"
                onClick={handleSubmitClick}
                title="Post comment"
              >
                <span className="send_icon">➤</span>
              </div>
            )}
            
            {/* Emoji picker */}
            <div
              className={`comment_circle_icon hover2 emoji_btn ${picker ? 'active' : ''}`}
              ref={emojiButtonRef}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                syncCaretFromInput();
                if (!picker && isMobileView()) {
                  textRef.current?.blur?.();
                }
                setPicker((prev) => !prev);
              }}
              title="Add emoji"
            >
              <i className="emoji_icon"></i>
            </div>
            
            {/* Camera icon for image upload */}
            <div
              className={`comment_circle_icon hover2 camera_btn ${commentImage ? 'has_image' : ''}`}
              onClick={() => imgInput.current.click()}
              title="Add photo"
            >
              <i className="camera_icon"></i>
            </div>
            
            {/* GIF button */}
            <div 
              className={`comment_circle_icon hover2 gif_btn ${showGifPicker ? 'active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!showGifPicker && isMobileView()) {
                  textRef.current?.blur?.();
                }
                setShowGifPicker((prev) => !prev);
              }}
              title="Add GIF"
            >
              <i className="gif_icon"></i>
            </div>
            
            {/* Sticker button */}
            <div 
              className={`comment_circle_icon hover2 sticker_btn ${showStickerPicker ? 'active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowStickerPicker((prev) => !prev);
              }}
              title="Add sticker"
            >
              <i className="sticker_icon"></i>
            </div>
          </div>
        </div>
      </div>

      {/* GIF picker portaled to body — trending GIFs on open, cancel works on mobile */}
      {showGifPicker &&
        createPortal(
          <div
            className="comment_gif_picker_overlay"
            onClick={closeGifPicker}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            <div
              className="comment_gif_picker"
              role="dialog"
              aria-modal="true"
              aria-label="Choose a GIF"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
            >
              <div className="gif_picker_header">
                <input
                  type="text"
                  placeholder="Search GIFs..."
                  value={gifSearch}
                  onChange={handleGifSearch}
                  className="gif_search_input"
                  ref={gifSearchInputRef}
                />
                <button
                  type="button"
                  className="gif_cancel_btn"
                  onClick={closeGifPicker}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="close_picker_btn"
                  onClick={closeGifPicker}
                  aria-label="Close GIF picker"
                >
                  ✕
                </button>
              </div>
              <div className="gif_results">
                {error && (
                  <div className="gif_error">
                    <span>{error}</span>
                    <div className="error_actions">
                      <button
                        type="button"
                        className="retry_btn"
                        onClick={retryGifSearch}
                        title="Retry search"
                      >
                        Retry
                      </button>
                      <button
                        type="button"
                        className="error_close"
                        onClick={() => setError("")}
                        title="Dismiss error"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                )}
                {gifLoading ? (
                  <div className="gif_loading">
                    <ClipLoader size={20} color="#1876f2" loading={true} />
                    <span>Loading GIFs...</span>
                  </div>
                ) : gifs.length > 0 ? (
                  <div className="gif_grid">
                    {gifs.map((gif) => (
                      <button
                        type="button"
                        key={gif.id}
                        className="gif_thumbnail_btn"
                        onClick={(e) => handleGifSelect(gif, e)}
                      >
                        <img
                          src={gif.images.fixed_height_small.url}
                          alt={gif.title || "GIF"}
                          className="gif_thumbnail"
                        />
                      </button>
                    ))}
                  </div>
                ) : gifSearch && !gifLoading ? (
                  <div className="no_gifs">No GIFs found</div>
                ) : (
                  <div className="gif_placeholder">Loading trending GIFs...</div>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Emoji picker portaled to body — avoids blocked taps + keeps keyboard closed on mobile */}
      {picker &&
        createPortal(
          <div
            className="comment_emoji_picker_overlay"
            onClick={closeEmojiPicker}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            <div
              className="comment_emoji_picker"
              ref={emojiPickerRef}
              role="dialog"
              aria-modal="true"
              aria-label="Choose an emoji"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
            >
              <div className="emoji_picker_header">
                <span>Choose an emoji</span>
                <button
                  type="button"
                  className="close_picker_btn"
                  onClick={closeEmojiPicker}
                  aria-label="Close emoji picker"
                >
                  ✕
                </button>
              </div>
              <Picker
                onEmojiClick={handleEmoji}
                disableAutoFocus={true}
                native={true}
                pickerStyle={{
                  width: "100%",
                  height: "240px",
                  boxShadow: "none",
                  border: "none",
                  background: "transparent",
                }}
              />
              <div className="emoji_picker_composer">
                <div
                  className={`emoji_picker_preview ${
                    text.trim() ? "" : "is_placeholder"
                  }`}
                  title={text}
                >
                  {text.trim() ? text : "Selected emojis appear here"}
                </div>
                <button
                  type="button"
                  className="emoji_picker_backspace"
                  onClick={deleteLastEmoji}
                  disabled={!text}
                  aria-label="Delete last emoji"
                  title="Delete"
                >
                  ⌫
                </button>
                <button
                  type="button"
                  className="emoji_picker_send"
                  onClick={sendFromEmojiPicker}
                  disabled={!text.trim() || loading}
                  aria-label="Send comment"
                  title="Send"
                >
                  {loading ? (
                    <ClipLoader size={14} color="#fff" loading={true} />
                  ) : (
                    "➤"
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Sticker picker portaled to body so mobile nav / post overflow cannot block taps */}
      {showStickerPicker &&
        createPortal(
          <div
            className="comment_sticker_picker_overlay"
            onClick={closeStickerPicker}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            <div
              className="comment_sticker_picker"
              role="dialog"
              aria-modal="true"
              aria-label="Choose a sticker"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
            >
              <div className="sticker_picker_header">
                <span>Choose a sticker</span>
                <div className="sticker_picker_header_actions">
                  <button
                    type="button"
                    className="sticker_cancel_btn"
                    onClick={closeStickerPicker}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="close_picker_btn"
                    onClick={closeStickerPicker}
                    aria-label="Close sticker picker"
                  >
                    ✕
                  </button>
                </div>
              </div>
              <div className="sticker_categories">
                {stickerCategories.map((category) => (
                  <div key={category.id} className="sticker_category">
                    <h4>{category.name}</h4>
                    <div className="sticker_grid">
                      {category.stickers.map((sticker, index) => (
                        <button
                          type="button"
                          key={`${category.id}-${index}`}
                          className="sticker_item"
                          onClick={(e) => handleStickerSelect(sticker, e)}
                        >
                          {sticker}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>,
          document.body
        )}
      
      {/* Enhanced image preview with send button */}
      {commentImage && (
        <div className="comment_img_preview enhanced">
          <div className="preview_header">
            <span className="preview_label notranslate">📷 Image attached</span>
            {imagePreview && (
              <span className="preview_info">
                {imagePreview.name} ({imagePreview.size} MB)
              </span>
            )}
          </div>
          <div className="preview_image_container">
            <img src={commentImage} alt="Comment preview" />
            
            {/* Action buttons for image */}
            <div className="image_action_buttons">
              {/* image_send_button removed per request; use main submit instead */}
              
              {/* Loading spinner for image */}
              {loading && (
                <div className="image_loading">
                  <ClipLoader size={20} color="#fff" loading={true} />
                  <span>Posting...</span>
                </div>
              )}
              
              {/* Remove button */}
              <div
                className="small_white_circle"
                onClick={removeImage}
                title="Remove image"
              >
                <i className="exit_icon"></i>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default CreateComment;
